/**
 * One-shot recipes for the noises people make: a sip, Hannu coming out of the
 * water and swinging at you, the shouts, Kempf mumbling, and the two things a
 * hungover man does that nobody wants a sample of.
 *
 * Same contract as `oneshots.ts` - `(v, t, g)`, schedule and return - and the
 * same ingredients out of `synth.ts`. Kept apart from the world's noises
 * because a voice is tuned by ear against other voices.
 */

import { noiseBurst, tone, type Voicing } from './synth';

/**
 * A pull from the bottle.
 *
 * Three glugs, each a short resonant blip a little lower than the last - that
 * falling pitch is the whole trick, because it is what a bottle emptying
 * actually sounds like and what a tap does not. Then the swallow underneath it,
 * and the sihahdus of the carbonation over the top of the lot.
 */
export function beerSip(v: Voicing, t: number, g: number): void {
  for (let i = 0; i < 3; i++) {
    const at = t + i * 0.082;
    tone(v, {
      type: 'sine',
      freq: 210 - i * 32,
      freqEnd: 118 - i * 20,
      at,
      duration: 0.075,
      peak: 0.42 * g,
      attack: 0.004,
    });
    noiseBurst(v, {
      at,
      duration: 0.06,
      peak: 0.14 * g,
      attack: 0.004,
      filter: 'bandpass',
      freq: 920 - i * 130,
      q: 6,
    });
  }
  noiseBurst(v, {
    at: t + 0.27,
    duration: 0.17,
    peak: 0.18 * g,
    attack: 0.03,
    filter: 'lowpass',
    freq: 520,
    freqEnd: 170,
  });

  // The sihahdus: the froth, running the length of the cue and outlasting the
  // swallow, because the fizz is the last thing you hear after the bottle
  // comes down. Two things keep it a hiss rather than a hit - an attack long
  // enough that it swells instead of clicking, and a band that climbs as it
  // fades, so the froth thins out to a whisper on its way to nothing.
  //
  // The peak is high for a noise burst because a bandpass this wide throws
  // most of the noise away: at 0.16 it measured three percent over the dry
  // cue, which is to say inaudible. The second number that matters is the
  // second: an exponential ramp is spent long before it reaches its end time,
  // so a full second is what it costs to still be audible at 0.44 s, where the
  // swallow stops. It is still shorter than BEER.cooldown, so two sips in a
  // row do not pile their froth on top of each other.
  noiseBurst(v, {
    at: t + 0.02,
    duration: 1.0,
    peak: 0.5 * g,
    attack: 0.08,
    filter: 'bandpass',
    freq: 3200,
    freqEnd: 5600,
    q: 0.9,
  });
}

export function hannuEmerge(v: Voicing, t: number, g: number): void {
  // Water leaving the palju in one movement, plus what comes up with him.
  noiseBurst(v, {
    at: t,
    duration: 0.85,
    peak: 0.5 * g,
    attack: 0.12,
    filter: 'bandpass',
    freq: 300,
    freqEnd: 2200,
    q: 0.8,
  });
  for (let i = 0; i < 4; i++) {
    tone(v, {
      type: 'sine',
      freq: 220 + v.rng() * 180,
      freqEnd: 900,
      at: t + 0.12 + i * 0.11 + v.rng() * 0.04,
      duration: 0.07,
      peak: 0.22 * g,
      attack: 0.004,
    });
  }
}

export function hannuSwipe(v: Voicing, t: number, g: number): void {
  noiseBurst(v, {
    at: t,
    duration: 0.26,
    peak: 0.6 * g,
    attack: 0.05,
    filter: 'bandpass',
    freq: 420,
    freqEnd: 2400,
    q: 1.1,
  });
  // The wet part, landing at the end of the arc.
  noiseBurst(v, {
    at: t + 0.17,
    duration: 0.14,
    peak: 0.5 * g,
    attack: 0.003,
    filter: 'lowpass',
    freq: 1600,
    freqEnd: 300,
    q: 0.8,
  });
}

/**
 * Wordless two-syllable yell, used only when the browser has no speech
 * synthesis. It carries the timing and the direction; the words are lost.
 */
export function shout(v: Voicing, t: number, g: number, distant: boolean): void {
  const base = distant ? 300 : 215;
  const formant = distant ? 1150 : 780;
  const vibrato = { depth: 7, hz: 5.5 };
  tone(v, {
    type: 'sawtooth',
    freq: base,
    freqEnd: base * 0.82,
    at: t,
    duration: 0.34,
    peak: 0.3 * g,
    attack: 0.035,
    vibrato,
    filter: { type: 'bandpass', freq: formant, q: 3.5 },
  });
  tone(v, {
    type: 'sawtooth',
    freq: base * 1.12,
    freqEnd: base * 0.7,
    at: t + 0.4,
    duration: 0.5,
    peak: 0.34 * g,
    attack: 0.045,
    vibrato,
    filter: { type: 'bandpass', freq: formant * 0.9, q: 3.5 },
  });
}

/**
 * Kempf, on the subject of the sauna being taken.
 *
 * `shout()` with different numbers would have been wrong for this one and it
 * is worth saying why, because that function already answers for four other
 * people. It is a *hail*: two syllables with a gap in the middle, thrown
 * across a yard, and it rises because it is trying to reach somebody. This man
 * is a stride away and he is not trying to reach anybody - he is muttering an
 * apology at a runner who is already leaning on the handle. So it is low,
 * level and unhurried, with the two syllables run together rather than spaced,
 * and the whole of it sits under everything else in the room.
 *
 * A fallback like the rest of them: `voice/kempf` is what actually says the
 * words. This only has to be unmistakably a man talking rather than a door.
 */
export function kempfMumble(v: Voicing, t: number, g: number): void {
  const base = 128;
  const vibrato = { depth: 3, hz: 4.5 };
  tone(v, {
    type: 'sawtooth',
    freq: base,
    freqEnd: base * 0.94,
    at: t,
    duration: 0.3,
    peak: 0.26 * g,
    attack: 0.05,
    vibrato,
    filter: { type: 'bandpass', freq: 520, q: 3 },
  });
  // The second half comes in under the first rather than after it, which is
  // what running two syllables together sounds like.
  tone(v, {
    type: 'sawtooth',
    freq: base * 0.95,
    freqEnd: base * 0.86,
    at: t + 0.26,
    duration: 0.42,
    peak: 0.22 * g,
    attack: 0.06,
    vibrato,
    filter: { type: 'bandpass', freq: 470, q: 3 },
  });
}

/**
 * Kimmo, on the subject of the window.
 *
 * Deliberately not `shout()` with different numbers. That one is a two-syllable
 * *call* - somebody across a yard getting your attention, which is why it has a
 * gap in the middle and rises at the end. This is one syllable, involuntary,
 * and it goes the other way: hardest at the front, falling for the whole of its
 * length, because it is not addressed to anybody. A yelp, not a hail.
 *
 * The rasp is what sells it as a person rather than a synthesiser: a band of
 * noise tracking the voice, which is roughly what a throat does when somebody
 * pushes more air through it than it was built for.
 *
 * A fallback, like every other cue in here - `sfx/kimmo-shout.m4a` is the
 * sound. This only has to be unmistakably a man who has hurt himself.
 */
export function kimmoShout(v: Voicing, t: number, g: number): void {
  const base = 240;
  // Straight in at full: nobody eases into this.
  tone(v, {
    type: 'sawtooth',
    freq: base * 1.35,
    freqEnd: base * 0.62,
    at: t,
    duration: 0.62,
    peak: 0.34 * g,
    attack: 0.012,
    vibrato: { depth: 11, hz: 7.5 },
    filter: { type: 'bandpass', freq: 820, q: 3.2 },
  });
  // The second formant, which is most of what makes a vowel a vowel.
  tone(v, {
    type: 'sawtooth',
    freq: base * 1.35,
    freqEnd: base * 0.62,
    at: t,
    duration: 0.58,
    peak: 0.14 * g,
    attack: 0.012,
    vibrato: { depth: 11, hz: 7.5 },
    filter: { type: 'bandpass', freq: 1720, q: 4.5 },
  });
  // Throat.
  noiseBurst(v, {
    at: t,
    duration: 0.5,
    peak: 0.12 * g,
    attack: 0.02,
    filter: 'bandpass',
    freq: 1500,
    freqEnd: 700,
    q: 1.1,
  });
}

/**
 * And the other answer: a mouthful of parsakaalia coming straight back.
 *
 * Two heaves and the splash, all of it sliding downwards, because the whole
 * job of this cue is to be unmistakably not the one above. The pitch falls,
 * the noise gets wetter, and it lasts about as long as the pause it is
 * announcing - see `AAMUPALA.vomitDuration`.
 */
export function oksennus(v: Voicing, t: number, g: number): void {
  for (const [delay, freq, length] of [
    [0, 210, 0.3],
    [0.34, 175, 0.42],
  ] as const) {
    // The retch: a throat, so a sawtooth under a low-pass rather than a tone.
    tone(v, {
      type: 'sawtooth',
      freq,
      freqEnd: freq * 0.5,
      at: t + delay,
      duration: length,
      peak: 0.34 * g,
      attack: 0.03,
      vibrato: { depth: 14, hz: 22 },
      filter: { type: 'lowpass', freq: 700, q: 1.1 },
    });
    // And what comes with it, on the way down.
    noiseBurst(v, {
      at: t + delay + 0.07,
      duration: length,
      peak: 0.4 * g,
      attack: 0.02,
      filter: 'bandpass',
      freq: 1300,
      freqEnd: 260,
      q: 0.5,
    });
  }
  // The splash on the kivetys, once, at the end of the second one.
  noiseBurst(v, {
    at: t + 0.62,
    duration: 0.34,
    peak: 0.3 * g,
    attack: 0.006,
    filter: 'lowpass',
    freq: 2200,
    freqEnd: 300,
    q: 0.6,
  });
}

/**
 * Kimmo, asleep, doing the other thing a man asleep in his own bed does.
 *
 * A sawtooth under a low-pass with the vibrato pushed up into the audio range,
 * and that last part is the whole recipe: at the 22 Hz `oksennus` uses, that
 * setting is a wobble on a note, and somewhere past 40 it stops modulating the
 * note and becomes the buzz itself. Everything else is pressure - the pitch
 * falls across the length of it because the pressure behind it does, and the
 * second one is short and higher because there is never as much left as the
 * first one suggests.
 */
export function fart(v: Voicing, t: number, g: number): void {
  // start, length, pitch, flap rate, loudness.
  for (const [delay, length, freq, flap, level] of [
    [0, 0.3, 92, 47, 1],
    [0.43, 0.06, 128, 62, 0.5],
  ] as const) {
    tone(v, {
      type: 'sawtooth',
      freq,
      freqEnd: freq * 0.62,
      at: t + delay,
      duration: length,
      peak: 0.5 * level * g,
      attack: 0.008,
      vibrato: { depth: freq * 0.55, hz: flap },
      filter: { type: 'lowpass', freq: 520, q: 1.4 },
    });
    // The air that comes with it. Narrow, and low enough to sit inside the
    // buzz rather than on top of it - a wideband hiss here would be a tyre.
    noiseBurst(v, {
      at: t + delay,
      duration: length,
      peak: 0.16 * level * g,
      attack: 0.01,
      filter: 'bandpass',
      freq: 420,
      freqEnd: 240,
      q: 1.2,
    });
  }
}
